import { useEffect, useState } from "react";
import { Outlet, useNavigate, NavLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Calendar, BarChart3, Package, Settings, LogOut, LayoutDashboard, Palette, ShoppingBag, ClipboardList, Users, UserSquare2, Webhook, Menu, X, ExternalLink, Gift, CreditCard, TrendingUp, Plug, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PushToggle } from "@/components/pwa/PushToggle";
import { Sheet, SheetContent, SheetClose } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import logo from "@/assets/logo-mente-sa.png";
import InstallAppButton from "@/components/InstallAppButton";

const navItems = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/agenda", icon: Calendar, label: "Agenda", end: false },
  { to: "/admin/services", icon: Package, label: "Serviços", end: false },
  { to: "/admin/packages", icon: Gift, label: "Pacotes", end: false },
  { to: "/admin/products", icon: ShoppingBag, label: "Produtos", end: false },
  { to: "/admin/orders", icon: ClipboardList, label: "Encomendas", end: false },
  { to: "/admin/staff", icon: Users, label: "Colaboradores", end: false },
  { to: "/admin/clients", icon: UserSquare2, label: "Clientes", end: false },
  { to: "/admin/client-form", icon: ClipboardList, label: "Editor da Ficha", end: false },
  { to: "/admin/content", icon: Palette, label: "Conteúdo", end: false },
  { to: "/admin/finances", icon: BarChart3, label: "Financeiro", end: false },
  { to: "/admin/metrics", icon: Activity, label: "Métricas do site", end: false },
  { to: "/admin/reports", icon: TrendingUp, label: "Relatórios & CRM", end: false },
  { to: "/admin/settings", icon: Settings, label: "Definições", end: false },
  { to: "/admin/api", icon: Webhook, label: "APIs", end: false },
  { to: "/admin/integrations", icon: Plug, label: "Integrações", end: false },
];


const SidebarContent = ({ onItemClick, signOut }: { onItemClick?: () => void; signOut: () => void }) => (
  <>
    <div className="p-5 border-b border-border">
      <a href="/" className="flex items-center gap-3">
        <img src={logo} alt="Mente Sã" className="h-10 w-10 rounded-full object-cover" />
        <div>
          <span className="font-display text-lg font-bold text-primary block">Mente Sã</span>
          <span className="text-[10px] text-muted-foreground">Painel Admin</span>
        </div>
      </a>
    </div>
    <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
      {navItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onItemClick}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`
          }
        >
          <item.icon size={18} />
          {item.label}
        </NavLink>
      ))}
    </nav>
    <div className="p-3 border-t border-border space-y-1">
      <a
        href="/pdv"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-primary hover:bg-primary/10 transition-colors w-full"
      >
        <CreditCard size={18} />
        Caixa (PDV)
      </a>
      <a
        href="/?edit=true"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-primary hover:bg-primary/10 transition-colors w-full"
      >
        <ExternalLink size={18} />
        Editor Visual
      </a>
      <InstallAppButton label="Instalar Admin" variant="ghost" fullWidth />
      <PushToggle className="w-full justify-start border-0" />
      <Button variant="ghost" className="w-full justify-start gap-3" onClick={signOut}>
        <LogOut size={18} /> Sair
      </Button>
    </div>

  </>
);

const AdminLayout = () => {
  const { user, isAdmin, loading, rolesLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    // Only redirect once the persisted session AND the role check have both
    // finished; otherwise a slow role lookup would kick a valid admin out.
    if (!loading && !rolesLoading && (!user || !isAdmin)) {
      navigate("/auth");
    }
  }, [user, isAdmin, loading, rolesLoading, navigate]);

  if (loading || rolesLoading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">A carregar...</div>;
  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      {!isMobile && (
        <aside className="w-64 border-r border-border bg-card flex flex-col shrink-0">
          <SidebarContent signOut={signOut} />
        </aside>
      )}

      {/* Mobile sidebar sheet */}
      {isMobile && (
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetContent side="left" className="w-64 p-0 flex flex-col">
            <SidebarContent onItemClick={() => setSidebarOpen(false)} signOut={signOut} />
          </SheetContent>
        </Sheet>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        {isMobile && (
          <div className="border-b border-border bg-card px-4 py-3 flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
              <Menu size={20} />
            </Button>
            <span className="font-display text-lg font-bold text-primary">Mente Sã</span>
          </div>
        )}
        <main className={`flex-1 p-4 md:p-8 overflow-y-auto ${isMobile ? "pb-24" : ""}`}>
          <Outlet />
        </main>
        {isMobile && (
          <nav aria-label="Navegação do painel" className="fixed bottom-0 inset-x-0 z-40 border-t border-border bg-card/95 backdrop-blur pb-[env(safe-area-inset-bottom)] flex">
            {[
              { to: "/admin", icon: LayoutDashboard, label: "Início", end: true },
              { to: "/admin/agenda", icon: Calendar, label: "Agenda", end: false },
              { to: "/admin/orders", icon: ClipboardList, label: "Pedidos", end: false },
              { to: "/admin/clients", icon: UserSquare2, label: "Clientes", end: false },
            ].map((t) => (
              <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 flex-1 py-2 text-[11px] font-medium ${isActive ? "text-primary" : "text-muted-foreground"}`}>
                <t.icon size={22} />{t.label}
              </NavLink>
            ))}
            <button type="button" onClick={() => setSidebarOpen(true)} className="flex flex-col items-center justify-center gap-0.5 flex-1 py-2 text-[11px] font-medium text-muted-foreground">
              <Menu size={22} />Mais
            </button>
          </nav>
        )}
      </div>
    </div>
  );
};

export default AdminLayout;
